// SCR-STAFF-909 (AP-PLT-07): خاتمه اشتراک با OTP مالک و تاریخ مؤثر پایان دوره (US-PLT-006، AC-PLT-006.1)، خروجی کامل داده با لینک زمان‌دار (AC-PLT-006.2) و
// انتقال مالکیت (US-PLT-007). خروجی و خاتمه در ReadOnly و Locked هم ممکن است (BR-PLT-006: «مشاهده و خروجی داده همیشه ممکن»).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type OwnershipTransferView, type TenantExportView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  ListPage,
  LoadingState,
  StatusChip,
  toJalali,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toCount } from './common';

export const SCR_STAFF_909 = 'SCR-STAFF-909';
export const TERMINATION_PATH = '/settings/termination';

const keys = {
  termination: (tenantId: string | undefined) => ['platform', 'tenant', 'termination', tenantId] as const,
  exports: (tenantId: string | undefined) => ['platform', 'tenant', 'exports', tenantId] as const,
  transfers: (tenantId: string | undefined) => ['platform', 'tenant', 'ownership-transfers', tenantId] as const,
};

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return faDigits(mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
}

function TerminationSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [otpId, setOtpId] = useState<string>();
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const view = useQuery({
    queryKey: keys.termination(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/tenant/termination')),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.termination(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: keys.exports(session?.tenantId) });
  };
  const start = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/termination:start')),
    onSuccess: (challenge) => setOtpId(challenge.otpId),
  });
  const confirm = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/termination:confirm', { body: { otpId: otpId ?? null, code, reason: reason || null } })),
    onSuccess: () => {
      setOtpId(undefined);
      setCode('');
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/termination:cancel')),
    onSuccess: refresh,
  });
  if (!access.can('plt.tenant.terminate')) {
    return null;
  }
  const data = view.data;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="termination">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:termination.state_title')}
      </Typography>
      {view.isPending ? <LoadingState rows={2} /> : null}
      {view.error ? <ErrorState error={view.error} onRetry={() => void view.refetch()} /> : null}
      {data ? (
        <Stack spacing={2}>
          <KeyValueList
            columns={3}
            items={[
              {
                labelKey: 'plt:subscription.fields.status',
                value: <StatusChip tone={data.status === 'Active' ? 'active' : 'pending'} labelKey={`plt:termination.status.${data.status}`} />,
                testId: 'termination-status',
              },
              { labelKey: 'plt:termination.paid_until', value: data.paidUntil ? toJalali(data.paidUntil) : '—' },
              { labelKey: 'plt:termination.effective_on', value: data.effectiveOn ? toJalali(data.effectiveOn) : '—', testId: 'termination-effective' },
            ]}
          />
          <Alert severity="info">{t('plt:termination.retention', { days: faDigits(toCount(data.retentionDays)) })}</Alert>
          {data.status === 'PendingTermination' ? (
            <Alert
              severity="warning"
              data-testid="termination-pending"
              action={
                <Button color="inherit" size="small" onClick={() => cancel.mutate()} disabled={cancel.isPending} data-testid="termination-cancel">
                  {t('plt:termination.cancel')}
                </Button>
              }
            >
              {t('plt:termination.pending_notice', { date: data.effectiveOn ? toJalali(data.effectiveOn) : '—' })}
            </Alert>
          ) : null}
          {data.status === 'Active' ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {t('plt:termination.request_text')}
              </Typography>
              <TextField label={t('plt:termination.reason')} value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={2} inputProps={{ maxLength: 500 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                <Button variant="outlined" color="error" onClick={() => start.mutate()} disabled={start.isPending} data-testid="termination-send-otp">
                  {t(otpId ? 'plt:termination.otp.resend' : 'plt:termination.otp.send')}
                </Button>
                {otpId ? (
                  <>
                    <TextField
                      label={t('plt:termination.otp.code')}
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': 'termination-code', autoComplete: 'one-time-code' } }}
                    />
                    <Button variant="contained" color="error" onClick={() => confirm.mutate()} disabled={!code || confirm.isPending} data-testid="termination-confirm">
                      {t('plt:termination.confirm')}
                    </Button>
                  </>
                ) : null}
              </Stack>
              {otpId ? <Alert severity="info">{t('plt:termination.otp.sent')}</Alert> : null}
            </Stack>
          ) : null}
          {start.error ? <ErrorState error={start.error} compact /> : null}
          {confirm.error ? <ErrorState error={confirm.error} compact /> : null}
          {cancel.error ? <ErrorState error={cancel.error} compact /> : null}
        </Stack>
      ) : null}
    </Paper>
  );
}

function ExportsSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string>();
  const exportsQuery = useQuery({
    queryKey: keys.exports(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/tenant/exports')),
    refetchInterval: (query) => (query.state.data?.items.some((item) => item.status === 'Queued' || item.status === 'Running') ? 5_000 : false),
  });
  const request = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/exports')),
    onSuccess: () => {
      setMessage('plt:termination.exports.requested');
      void queryClient.invalidateQueries({ queryKey: keys.exports(session?.tenantId) });
    },
  });
  const relink = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/platform/tenant/exports/{id}:relink', { params: { path: { id } } })),
    onSuccess: () => {
      setMessage('plt:termination.exports.relinked');
      void queryClient.invalidateQueries({ queryKey: keys.exports(session?.tenantId) });
    },
  });
  if (!access.can('plt.tenant.export')) {
    return null;
  }
  const columns: DataTableColumn<TenantExportView>[] = [
    { id: 'requested', headerKey: 'plt:termination.exports.requested_at', cell: (row) => <JalaliDate value={row.requestedAt} withTime /> },
    { id: 'reason', headerKey: 'plt:termination.reason', cell: (row) => t(`plt:termination.exports.reason.${row.reason}`, { defaultValue: row.reason }) },
    {
      id: 'status',
      headerKey: 'plt:termination.exports.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Ready' ? 'active' : row.status === 'Failed' ? 'error' : row.status === 'Purged' ? 'inactive' : 'pending'}
          labelKey={`plt:termination.exports.states.${row.status}`}
        />
      ),
    },
    { id: 'size', headerKey: 'plt:termination.exports.size', cell: (row) => (row.size ? <bdi dir="ltr">{formatSize(toCount(row.size))}</bdi> : '—') },
    { id: 'parts', headerKey: 'plt:termination.exports.parts', cell: (row) => <bdi dir="ltr">{row.parts.map((part) => part.module).join(' · ') || '—'}</bdi> },
    { id: 'expires', headerKey: 'plt:termination.exports.link_expires', cell: (row) => (row.linkExpiresAt ? <JalaliDate value={row.linkExpiresAt} withTime /> : '—') },
    {
      id: 'actions',
      headerKey: 'common:actions.title',
      cell: (row) =>
        row.status === 'Ready' ? (
          <Button size="small" onClick={() => relink.mutate(row.id)} disabled={relink.isPending} data-testid={`export-relink-${row.id}`}>
            {t('plt:termination.exports.relink')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="exports">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:termination.exports.title')}
      </Typography>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('plt:termination.exports.hint')}
        </Typography>
        <Button variant="outlined" onClick={() => request.mutate()} disabled={request.isPending} data-testid="export-request" sx={{ alignSelf: 'flex-start' }}>
          {t('plt:termination.exports.request')}
        </Button>
        {request.error ? <ErrorState error={request.error} compact /> : null}
        {relink.error ? <ErrorState error={relink.error} compact /> : null}
        {message ? <Alert severity="success">{t(message)}</Alert> : null}
        <DataTable
          captionKey="plt:termination.exports.caption"
          columns={columns}
          rows={exportsQuery.data?.items}
          isLoading={exportsQuery.isPending}
          error={exportsQuery.error}
          onRetry={() => void exportsQuery.refetch()}
          getRowId={(row) => row.id}
          emptyTitleKey="plt:termination.exports.empty"
        />
      </Stack>
    </Paper>
  );
}

function OwnershipSection() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [mobile, setMobile] = useState('');
  const [pending, setPending] = useState<{ id: string; otpId: string }>();
  const [code, setCode] = useState('');
  const [invited, setInvited] = useState(false);
  const transfers = useQuery({
    queryKey: keys.transfers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/tenant/ownership-transfers')),
    enabled: access.can('plt.tenant.transfer_ownership'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.transfers(session?.tenantId) });
  const start = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/platform/tenant/ownership-transfers', { body: { newOwnerMobile: mobile } })),
    onSuccess: (started) => {
      setPending({ id: started.transfer.id, otpId: started.otp.otpId });
      setInvited(false);
      refresh();
    },
  });
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/tenant/ownership-transfers/{id}:confirm', {
          params: { path: { id: pending?.id ?? '' } },
          body: { otpId: pending?.otpId ?? null, code },
        }),
      ),
    onSuccess: () => {
      setPending(undefined);
      setCode('');
      setInvited(true);
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/platform/tenant/ownership-transfers/{id}:cancel', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  if (!access.can('plt.tenant.transfer_ownership')) {
    return null;
  }
  const open = (status: string) => status === 'AwaitingOwnerOtp' || status === 'AwaitingNewOwner' || status === 'AwaitingApproval';
  const columns: DataTableColumn<OwnershipTransferView>[] = [
    { id: 'requested', headerKey: 'plt:termination.ownership.requested_at', cell: (row) => <JalaliDate value={row.requestedAt} withTime /> },
    { id: 'mobile', headerKey: 'plt:termination.ownership.mobile', cell: (row) => <bdi dir="ltr">{faDigits(row.newOwnerMobile)}</bdi> },
    {
      id: 'status',
      headerKey: 'plt:termination.ownership.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Completed' ? 'active' : open(row.status) ? 'pending' : 'inactive'}
          labelKey={`plt:termination.ownership.states.${row.status}`}
        />
      ),
    },
    { id: 'expires', headerKey: 'plt:termination.ownership.expires', cell: (row) => <JalaliDate value={row.expiresAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'common:actions.title',
      cell: (row) =>
        open(row.status) ? (
          <Button size="small" color="warning" onClick={() => cancel.mutate(row.id)} disabled={cancel.isPending} data-testid={`transfer-cancel-${row.id}`}>
            {t('plt:termination.ownership.cancel')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="ownership">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('plt:termination.ownership.title')}
      </Typography>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('plt:termination.ownership.hint')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
          <TextField
            label={t('plt:termination.ownership.new_mobile')}
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            slotProps={{ htmlInput: { inputMode: 'tel', dir: 'ltr', 'data-testid': 'transfer-mobile' } }}
          />
          <Button variant="outlined" onClick={() => start.mutate()} disabled={!mobile || start.isPending} data-testid="transfer-start">
            {t('plt:termination.ownership.start')}
          </Button>
        </Stack>
        {pending ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
            <TextField
              label={t('plt:termination.otp.code')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': 'transfer-code', autoComplete: 'one-time-code' } }}
            />
            <Button variant="contained" onClick={() => confirm.mutate()} disabled={!code || confirm.isPending} data-testid="transfer-confirm">
              {t('plt:termination.ownership.confirm')}
            </Button>
          </Stack>
        ) : null}
        {pending ? <Alert severity="info">{t('plt:termination.otp.sent')}</Alert> : null}
        {invited ? <Alert severity="success">{t('plt:termination.ownership.invited')}</Alert> : null}
        {start.error ? <ErrorState error={start.error} compact /> : null}
        {confirm.error ? <ErrorState error={confirm.error} compact /> : null}
        {cancel.error ? <ErrorState error={cancel.error} compact /> : null}
        <DataTable
          captionKey="plt:termination.ownership.caption"
          columns={columns}
          rows={transfers.data?.items}
          isLoading={transfers.isPending}
          error={transfers.error}
          onRetry={() => void transfers.refetch()}
          getRowId={(row) => row.id}
          emptyTitleKey="plt:termination.ownership.empty"
        />
      </Stack>
    </Paper>
  );
}

export function TerminationPage() {
  return (
    <ListPage screenId={SCR_STAFF_909} titleKey="plt:termination.title" subtitleKey="plt:termination.subtitle">
      <Stack spacing={2}>
        <ExportsSection />
        <TerminationSection />
        <OwnershipSection />
      </Stack>
    </ListPage>
  );
}
